import { ReactElement, useEffect, useState } from 'react';
import { UIModal, UIModalContent, UIModalFooter, UIModalHeader } from '@/ui';
import { useInvites } from '../providers';
import { useCompaniesTranslation } from '../hooks';
import { Invite } from './invite';
import { useAcceptInviteAction, useDeclineInviteAction } from '../actions';
import { NoInvites } from './no-invites';

type InvitesModalProps = {
    isOpen: boolean;
    onClose: () => void;
};
export function InvitesModal(props: InvitesModalProps): ReactElement {
    const { isOpen, onClose } = props;

    return (
        <UIModal isOpen={isOpen} onClose={onClose}>
            {isOpen ? <Content {...props} /> : null}
        </UIModal>
    );
}

function Content(props: InvitesModalProps): ReactElement {
    const { onClose } = props;

    const { invites } = useInvites();

    const { t } = useCompaniesTranslation();
    const acceptInvite = useAcceptInviteAction();
    const declineInvite = useDeclineInviteAction();
    const [acceptLoading, setAcceptLoading] = useState('');
    const [declineLoading, setDeclineLoading] = useState('');

    useEffect(() => {
        if (invites.length === 0) {
            onClose();
        }
    }, [invites.length, onClose]);

    return (
        <>
            <UIModalHeader title={t('components.invites_modal.title')} />
            <UIModalContent>
                <div
                    style={{
                        display: 'flex',
                        maxHeight: 400,
                        flexDirection: 'column',
                        overflowY: 'auto',
                        gap: 16
                    }}
                >
                    {invites.map((invite) => (
                        <Invite
                            key={invite.id}
                            name={invite.companyName}
                            image={invite.companyImage}
                            acceptLoading={acceptLoading === invite.id}
                            declineLoading={declineLoading === invite.id}
                            disabled={
                                acceptLoading !== '' || declineLoading !== ''
                            }
                            onAccept={() => {
                                setAcceptLoading(invite.id);
                                acceptInvite
                                    .acceptInvite(invite.id)
                                    .then((response) => {
                                        window.location.href = `/company/${response.data.company.id}`;
                                    })
                                    .catch(() => {}) // Error handled in the action
                                    .finally(() => setAcceptLoading(''));
                            }}
                            onDecline={() => {
                                setDeclineLoading(invite.id);
                                declineInvite
                                    .declineInvite(invite.id)
                                    .catch(() => {}) // Error handled in the action
                                    .finally(() => setDeclineLoading(''));
                            }}
                        />
                    ))}
                    {invites.length === 0 && <NoInvites />}
                </div>
            </UIModalContent>
            <UIModalFooter
                actions={[
                    {
                        text: t('shared.labels.close'),
                        onClick: onClose
                    }
                ]}
            />
        </>
    );
}
